import { useState, useEffect } from "react";
import { api } from "../api.js";
import { card, labelStyle, inputStyle, btnPrimary, btnSecondary, SectionTitle, Badge } from "./ui.js";

export default function SettingsTab() {
  const [me, setMe] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // 企業名編集用
  const [editCompany, setEditCompany] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [savingCompany, setSavingCompany] = useState(false);
  const [companyMsg, setCompanyMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const handleSaveCompanyName = async () => {
    const trimmed = companyName.trim();
    if (!trimmed) {
      setCompanyMsg({ type: "error", text: "企業名を入力してください" });
      return;
    }
    setSavingCompany(true);
    setCompanyMsg(null);
    try {
      await api.updateMe({ companyName: trimmed });
      setMe({ ...me, companyName: trimmed });
      setEditCompany(false);
      setCompanyMsg({ type: "success", text: "企業名を更新しました" });
    } catch (e: any) {
      setCompanyMsg({ type: "error", text: e.message });
    } finally {
      setSavingCompany(false);
    }
  };

  useEffect(() => {
    api.getMe()
      .then((t: any) => setMe(t))
      .catch((e: any) => setMessage({ type: "error", text: e.message }))
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async () => {
    if (!newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      setMessage({ type: "error", text: "有効なメールアドレスを入力してください" });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const res = await api.updateEmail(newEmail);
      setMessage({ type: "success", text: res.message || "確認メールを送信しました" });
      setNewEmail("");
      setShowForm(false);
      // 再取得（pendingEmail反映）
      const t = await api.getMe();
      setMe(t);
    } catch (e: any) {
      setMessage({ type: "error", text: e.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#6b6b68" }}>読み込み中...</div>;

  return (
    <>
      <SectionTitle>アカウント設定</SectionTitle>

      {message && (
        <div style={{
          padding: "12px 16px", borderRadius: 8, fontSize: 14, marginBottom: 16,
          background: message.type === "success" ? "#E6F6EC" : "#FCEBEB",
          color: message.type === "success" ? "#166534" : "#A32D2D",
        }}>
          {message.text}
        </div>
      )}

      <div style={card}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 18 }}>ログイン用メールアドレス</div>

        <div style={{ padding: "12px 0", borderBottom: "1px solid #f0efeb", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: "#6b6b68", marginBottom: 4 }}>現在のログイン用メールアドレス</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 15, fontWeight: 500 }}>{me?.email || "未設定"}</div>
              {me?.emailVerified && <Badge bg="#E6F6EC" color="#166534">検証済み</Badge>}
            </div>
          </div>
          {!showForm && (
            <button onClick={() => setShowForm(true)} style={{ ...btnSecondary, fontSize: 13, padding: "8px 16px" }}>
              変更
            </button>
          )}
        </div>

        {me?.pendingEmail && (
          <div style={{ padding: "12px 0", borderBottom: "1px solid #f0efeb" }}>
            <div style={{ fontSize: 13, color: "#6b6b68", marginBottom: 4 }}>変更申請中</div>
            <div style={{ fontSize: 14 }}>
              {me.pendingEmail} <Badge bg="#FAEEDA" color="#854F0B">確認メール送信済</Badge>
            </div>
            <div style={{ fontSize: 12, color: "#6b6b68", marginTop: 4 }}>
              新メール内のリンクをクリックすると変更が完了します。有効期限: 24時間
            </div>
          </div>
        )}

        {showForm && (
          <div style={{ paddingTop: 18 }}>
            <div style={labelStyle}>新しいログイン用メールアドレス</div>
            <input
              style={{ ...inputStyle, marginBottom: 14 }}
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="new@example.com"
            />
            <div style={{ padding: "12px 14px", background: "#faf9f7", borderRadius: 8, fontSize: 13, color: "#6b6b68", marginBottom: 14, lineHeight: 1.6 }}>
              新しいログイン用メールアドレスに確認リンクを送信します。リンクをクリックするまで変更は完了しません。
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button style={{ ...btnPrimary, opacity: !newEmail || saving ? 0.5 : 1 }} disabled={!newEmail || saving} onClick={handleSubmit}>
                {saving ? "送信中..." : "確認メールを送信"}
              </button>
              <button style={btnSecondary} onClick={() => { setShowForm(false); setNewEmail(""); setMessage(null); }}>
                キャンセル
              </button>
            </div>
          </div>
        )}
      </div>

      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>会社情報</div>
          {!editCompany && (
            <button
              style={{ ...btnSecondary, padding: "6px 14px", fontSize: 13 }}
              onClick={() => {
                setCompanyName(me?.companyName || "");
                setEditCompany(true);
                setCompanyMsg(null);
              }}
            >
              企業名を編集
            </button>
          )}
        </div>

        {companyMsg && (
          <div style={{
            padding: "10px 14px",
            borderRadius: 8,
            background: companyMsg.type === "success" ? "#E1F5EE" : "#FCEBEB",
            color: companyMsg.type === "success" ? "#0F6E56" : "#A32D2D",
            fontSize: 14,
            marginBottom: 12,
          }}>
            {companyMsg.text}
          </div>
        )}

        <div style={{ padding: "8px 0", display: "flex", gap: 20 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: "#6b6b68", marginBottom: 4 }}>企業名</div>
            {editCompany ? (
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="例: 株式会社サンプル"
                  style={{ ...inputStyle, flex: 1 }}
                  maxLength={255}
                  autoFocus
                />
                <button
                  style={{ ...btnPrimary, padding: "8px 16px", fontSize: 13, opacity: savingCompany ? 0.5 : 1 }}
                  disabled={savingCompany}
                  onClick={handleSaveCompanyName}
                >
                  {savingCompany ? "保存中..." : "保存"}
                </button>
                <button
                  style={{ ...btnSecondary, padding: "8px 16px", fontSize: 13 }}
                  onClick={() => { setEditCompany(false); setCompanyMsg(null); }}
                >
                  キャンセル
                </button>
              </div>
            ) : (
              <div style={{ fontSize: 15, fontWeight: 500 }}>{me?.companyName || "—"}</div>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: "#6b6b68", marginBottom: 4 }}>HubSpot Portal ID</div>
            <div style={{ fontSize: 15, fontWeight: 500, fontFamily: "monospace" }}>{me?.portalId || "—"}</div>
          </div>
        </div>

        {/* 氏名の表示順設定（コンタクトの姓・名の並び順） */}
        <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid #f0efeb" }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>コンタクトの氏名表示順</div>
          <div style={{ fontSize: 12, color: "#6b6b68", marginBottom: 12, lineHeight: 1.7 }}>
            関連コンタクトから署名者を選ぶ際、HubSpot の「名（firstname）」「姓（lastname）」をどの順番で組み合わせて表示するかを指定します。
            <br />
            <span style={{ color: "#999" }}>※ HubSpot に「姓」と「名」を逆に登録している場合は「名→姓」を選択してください。</span>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {[
              { value: "lastname_firstname", label: "姓 → 名（日本式・推奨）" },
              { value: "firstname_lastname", label: "名 → 姓" },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={async () => {
                  try {
                    await api.updateSettings("contact_name_order", { contactNameOrder: opt.value });
                    setMe({ ...me, contactNameOrder: opt.value });
                    setCompanyMsg({ type: "success", text: "氏名表示順を更新しました" });
                  } catch (e: any) {
                    setCompanyMsg({ type: "error", text: e.message });
                  }
                }}
                style={{
                  padding: "8px 16px",
                  fontSize: 13,
                  borderRadius: 8,
                  border: "1px solid",
                  cursor: "pointer",
                  background: (me?.contactNameOrder || "lastname_firstname") === opt.value ? "#534AB7" : "white",
                  color: (me?.contactNameOrder || "lastname_firstname") === opt.value ? "white" : "#1a1a1a",
                  borderColor: (me?.contactNameOrder || "lastname_firstname") === opt.value ? "#534AB7" : "#e8e7e3",
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
